import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import HoverDropdown from '@/components/nav/HoverDropdown';
import { Menu, X } from 'lucide-react';
import { Link, NavLink } from '@/lib/router-compat';
import ContactLink from '@/components/ContactLink';
import AkivaLogo from '@/components/AkivaLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SiteSearch } from '@/components/SiteSearch';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const industriesItems = [
    { label: 'Commercial', href: '/commercial' },
    { label: 'Government', href: '/government' }
  ];

  const solutionsServices = [
    { label: 'All Services', href: '/services' },
    { label: 'AI Engineering', href: '/service/ai-engineering' },
    { label: 'Enterprise IT', href: '/service/enterprise-it' },
    { label: 'Program Management', href: '/service/program-management' },
    { label: 'Strategy Consulting', href: '/service/strategy-consulting' },
    { label: 'Supply Chain Management', href: '/service/supply-chain' },
    { label: 'Digital & AI Transformation', href: '/service/digital-transformation' }
  ];

  const solutionsProducts = [
    { label: 'All Products', href: '/products' },
    { label: 'Software', href: '/products/software' },
    { label: 'Hardware', href: '/products/hardware' },
    { label: 'Compute', href: '/products/compute' }
  ];

  const companyItems = [
    { label: 'About', href: '/about' },
    { label: 'Partnerships', href: '/partnership' },
    { label: 'Contract Vehicles', href: '/contract-vehicles' },
    { label: 'Certifications', href: '/certifications' },
    { label: 'News', href: '/news' }
  ];

  // Recruitment audience: people who are not AKIVA yet. Rendered as the right
  // column of the Company dropdown, the same two-column shape Solutions uses.
  const careersItems = [
    { label: 'Careers', href: '/careers' },
    { label: 'AI Innovation Network', href: '/ai-network' },
  ];



  // Shared sizing for every desktop header dropdown panel and its rows.
  // One shape only now: the two-column panel used by both Solutions
  // (Services | Products) and Company (Company | Careers).
  const navWideMenuClass = 'z-50 grid grid-cols-[minmax(0,1fr)_180px] w-[430px] max-w-[calc(100vw-2rem)] gap-4 p-2.5';
  const navItemClass = 'px-2 py-1';
  const navLabelClass = 'px-2 pb-1 pt-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground';



  return (
    <nav className={`fixed top-0 w-full z-50 transition-colors duration-200 ${isScrolled ? 'bg-background/85 backdrop-blur-md' : 'bg-transparent'}`}>
      <div className="container-max">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex items-center pl-2">
            <Link
              to="/"
              className="flex items-center"
              aria-label="AKIVA Home"
              onClick={(e) => {
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // Land at top of home page rather than restoring prior scroll
                  window.scrollTo({ top: 0, behavior: 'auto' });
                }
              }}
            >
              <AkivaLogo className="h-7 transition-transform duration-200" />
            </Link>
          </div>

          {/* Right-aligned Navigation */}
          <div className="hidden md:flex items-center space-x-6">

            {/* Industries Direct Links */}
            <NavLink
              to="/commercial"
              className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground transition-colors duration-200 text-sm font-medium`}
            >
              Commercial
            </NavLink>
            <NavLink
              to="/government"
              className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground transition-colors duration-200 text-sm font-medium`}
            >
              Government
            </NavLink>

            {/* Solutions Dropdown */}
            <HoverDropdown label="Solutions" contentClassName={navWideMenuClass}>
              <div role="group" aria-label="Services">
                <DropdownMenuLabel className={navLabelClass}>
                  Services
                </DropdownMenuLabel>
                {solutionsServices.map((item) => (
                  <DropdownMenuItem key={item.label} className={navItemClass} asChild>
                    <Link to={item.href} className="cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </div>
              <div role="group" aria-label="Products" className="border-l border-border pl-4">
                <DropdownMenuLabel className={navLabelClass}>
                  Products
                </DropdownMenuLabel>
                {solutionsProducts.map((item) => (
                  <DropdownMenuItem key={item.label} className={navItemClass} asChild>
                    <Link to={item.href} className="cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </div>
            </HoverDropdown>

            {/* Company Dropdown: content on the left, recruitment on the right */}
            <HoverDropdown label="Company" contentClassName={navWideMenuClass}>
              <div role="group" aria-label="Company">
                <DropdownMenuLabel className={navLabelClass}>
                  Company
                </DropdownMenuLabel>
                {companyItems.map((item) => (
                  <DropdownMenuItem key={item.label} className={navItemClass} asChild>
                    {item.href.startsWith('http') ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                        {item.label}
                      </a>
                    ) : (
                      <Link to={item.href} className="cursor-pointer">
                        {item.label}
                      </Link>
                    )}
                  </DropdownMenuItem>
                ))}
              </div>
              <div role="group" aria-label="Join Us" className="border-l border-border pl-4">
                <DropdownMenuLabel className={navLabelClass}>
                  Join Us
                </DropdownMenuLabel>
                {careersItems.map((item) => (
                  <DropdownMenuItem key={item.label} className={navItemClass} asChild>
                    <Link to={item.href} className="cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </div>
            </HoverDropdown>

            {/* One door for every portal: the page itself decides which
                portal a person belongs to once their password is correct. */}
            <NavLink
              to="/sign-in"
              className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground transition-colors duration-200 text-sm font-medium`}
            >
              Sign in
            </NavLink>



            <ContactLink
              className="text-muted-foreground border-2 border-muted-foreground hover:text-foreground hover:border-foreground transition-colors duration-200 text-sm font-medium rounded-md px-4 py-2"
            >
              Contact
            </ContactLink>

            <SiteSearch />
            <ThemeToggle />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          // The scrolled header's backdrop-filter establishes a containing
          // block. Use viewport height so its 4rem box cannot collapse the menu.
          <div
            id="mobile-navigation"
            className="md:hidden fixed left-0 right-0 top-16 h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] bg-card"
          >
            <div className="px-2 pt-2 pb-6 space-y-1 bg-card border-t border-border rounded-b-xl">

              {/* Industries Direct Links */}
              <NavLink
                to="/commercial"
                className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block px-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                onClick={() => setIsMenuOpen(false)}
              >
                Commercial
              </NavLink>
              <NavLink
                to="/government"
                className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block px-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                onClick={() => setIsMenuOpen(false)}
              >
                Government
              </NavLink>

              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">
                Solutions - Services
              </div>
              {solutionsServices.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">
                Solutions - Products
              </div>
              {solutionsProducts.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">
                Company
              </div>
              {companyItems.map((item) => (
                item.href.startsWith('http') ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-md transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.label}
                    to={item.href}
                    className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                )
              ))}

              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">
                Join Us
              </div>
              {careersItems.map((item) => (
                item.href.includes('#') ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-muted-foreground block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.label}
                    to={item.href}
                    className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                )
              ))}

              <NavLink
                to="/sign-in"
                className={({ isActive }) => `${isActive ? 'text-foreground' : 'text-muted-foreground'} block pl-6 pr-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200`}
                onClick={() => setIsMenuOpen(false)}
              >
                Sign in
              </NavLink>


              <ContactLink
                className="text-muted-foreground block w-full text-left px-3 py-2 hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200 mt-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </ContactLink>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
